import appConstants from '../../../../../appConstants';

export const calculateSeconds = (endTime, startTime) => {
  if (!endTime || !startTime) {
    return appConstants.PICKUP_TIMING_CONSTANTS.TIMESTAMP_NOT_AVAILABLE;
  }
  return Number(((endTime - startTime) / 1000).toFixed(2));
};

// Convert timestamp to ISO string or return null if invalid
export const toISOStringOrNull = (timestamp) =>
  timestamp ? new Date(timestamp).toISOString() : null;

// Generates unique pickup operation tracking ID
export const generatePickupOperationTrackingId = ({actionType, driverId, pickupIds}) =>
  `${actionType}_${driverId}_${pickupIds.join('_')}_${Date.now()}`;

export const createDefaultPickupOperation = ({ actionType, driverId, pickupIds }) => {
  const pickupOperationTimeTrackingId = generatePickupOperationTrackingId({ actionType, driverId, pickupIds });
  
  return {
    actionType,
    pickupIds,
    driverId,
    pickupOperationTimeTrackingId,
    pickupOperationStartTime: new Date(),
    pickupOperationFinishTime: null,
    timestamps: {
      pickupOperationApi: {
        // Track assign/unassign/reassign API call and response
        requestStart: Date.now(), // When assign/unassign/reassign API is called
      response: null // When API responds with success/failure
    },
    dispatchRouteApi: {
      deduplicationStart: null, // When getDataWithDeduplication is called
      requestStart: null, // When actual getAllDispatchRoutes API call starts
      response: null // When getAllDispatchRoutes responds
    },
    signalREvents: [],
    loaderClosed: null
  },
  // Browser performance data
  browserPerformance: {
    pickupOperationApi: null, // Browser performance for pickup operation API
    dispatchRouteApi: null,   // Browser performance for dispatch route API
  },
  signalREventsReceived: 0,
  totalSignalREventsExpected: pickupIds.length,
  completed: false,
  failureReason: null
  };
};


const getPickupOperationMetaData = (pickupOperation) => {
  const { timestamps, pickupOperationStartTime, pickupOperationFinishTime, pickupOperationTimeTrackingId } = pickupOperation;
  const { signalREvents, pickupOperationApi, dispatchRouteApi, loaderClosed } = timestamps;

  const firstSignalR = signalREvents[0]?.timestamp;
  const lastSignalR = signalREvents[pickupOperation.signalREventsReceived - 1]?.timestamp;

  return {
    // Operation identification
    pickupOperationTimeTrackingId,
    
    // Overall operation timing
    startTime: toISOStringOrNull(pickupOperationStartTime),
    finishTime: toISOStringOrNull(pickupOperationFinishTime),
    totalDurationSec: pickupOperationFinishTime
      ? calculateSeconds(pickupOperationFinishTime.getTime(), pickupOperationStartTime.getTime())
      : undefined,

    // Stage 1: Initial pickup operation API timing
    pickupOperationApiStartTime: toISOStringOrNull(pickupOperationApi.requestStart),
    pickupOperationApiResponseTime: toISOStringOrNull(pickupOperationApi.response),

    // Stage 2: SignalR event timing
    firstSignalRReceivedTime: toISOStringOrNull(firstSignalR),
    lastSignalRReceivedTime: toISOStringOrNull(lastSignalR),

    // Stage 3: Dispatch routes API timing
    dispatchRoutesApiStartTime: toISOStringOrNull(dispatchRouteApi.requestStart),
    dispatchRoutesApiResponseTime: toISOStringOrNull(dispatchRouteApi.response),
    dispatchRoutesApiStalledDurationSec:
      dispatchRouteApi.requestStart && dispatchRouteApi.deduplicationStart
        ? calculateSeconds(dispatchRouteApi.requestStart, dispatchRouteApi.deduplicationStart)
        : 0,

    // Stage 4: UI interaction timing
    loaderClosedTime: toISOStringOrNull(loaderClosed)
  };
};

export const calculatePickupOperationMetrics = (pickupOperation) => {
  if (!pickupOperation) return null;
  const { timestamps, actionType, driverId, pickupIds, failureReason } = pickupOperation;
  const { signalREvents, pickupOperationApi, dispatchRouteApi, loaderClosed } = timestamps;

  const firstSignalR = signalREvents[0]?.timestamp;
  const lastSignalR = signalREvents[pickupOperation.signalREventsReceived - 1]?.timestamp;

  return {
    actionType,
    driverId,
    pickupIds,

    // Time taken for initial assignment API to respond with success
    pickupOperationRequestToResponseTimeSec: calculateSeconds(
      pickupOperationApi.response,
      pickupOperationApi.requestStart
    ),

    // Delay between API success and first SignalR message
    timeFromApiSuccessToFirstSignalRSec: calculateSeconds(firstSignalR, pickupOperationApi.response),

    // Total time from API success until all expected SignalR received
    timeUntilAllSignalRMessagesReceivedSec: calculateSeconds(lastSignalR, pickupOperationApi.response),

    // Wait time between last SignalR and starting dispatch route API
    delayBetweenLastSignalRAndDispatchRouteApiSec: calculateSeconds(dispatchRouteApi.requestStart, lastSignalR),

    // Time taken to fetch updated dispatch routes
    dispatchRouteApiFetchDurationSec: calculateSeconds(dispatchRouteApi.response, dispatchRouteApi.requestStart),

    // Total time the loading dialog was shown to user
    loaderClosedAtSec: calculateSeconds(loaderClosed, pickupOperationApi.requestStart),

    // Complete operation duration from start to finish
    totalDurationSec: calculateSeconds(
      dispatchRouteApi.response || loaderClosed,
      pickupOperationApi.requestStart
    ),

    // Browser performance data
    browserPerformance: pickupOperation.browserPerformance,

    // Operation metadata including timing details
    metaData: getPickupOperationMetaData(pickupOperation),

    failureReason
  };
};
